<template>
  <div class="auth-wrapper">
    <div class="auth-container d-flex primary">
      <div class="logo-container">
        <div class="logo">
          <img src="@/assets/logos/cdseo-logo-white.png" />
        </div>
      </div>

      <div class="content-container auth-content-container">
        <div class="auth-inner">
          <div class="auth-top d-flex justify-end">
            <a
              class="need-access-link"
              :href="needAccessUrl"
            >Need access?</a>
          </div>

          <div class="auth-body">
            <div class="auth-block">
              <div class="text-center logo-container-sm mb-8">
                <div class="logo">
                  <img src="@/assets/logos/cdseo-logo-blue.png" />
                </div>
              </div>

              <div class="auth-subtitle mb-1">{{ register ? 'Register' : 'Sign In' }}</div>
              <h1 class="auth-title mb-2" v-if="!register">
                Access Your Workspace
              </h1>
              <h1 class="auth-title mb-2" v-else>Register Your Organization</h1>
              <div class="auth-lead mb-8">Agentic Commerce Optimization for the AI Era</div>

              <v-alert
                prominent
                dense
                v-if="$route.query._cr"
                color="success"
                dark
                class="mb-8"
                icon="fa-brands fa-shopify"
              >
                Complete the setup process by choosing how you want to sign in to your workspace.
              </v-alert>

              <v-btn
                block
                x-large
                color="primary"
                class="text-none font-weight-bold text-body-1"
                :disabled="loading"
                :href="googleAuthUrl"
              >
                <img src="@/assets/icon-google.svg" height="20" class="mr-3" />
                Continue with Google
              </v-btn>

              <div class="auth-divider d-flex align-center my-6">
                <v-divider />
                <span class="auth-divider-label">Or Email</span>
                <v-divider />
              </div>

              <v-form
                class="auth-form"
                @submit.prevent="submit"
              >
                <div class="field-label mb-2">Work Email</div>
                <v-text-field
                  v-if="!requested"
                  placeholder="you@example.com"
                  outlined
                  v-model="email"
                  hide-details
                  class="mb-4"
                  @input="requested = false"
                  :disabled="loading"
                  autocomplete="email"
                ></v-text-field>

                <v-text-field
                  v-if="requested"
                  placeholder="Enter your passcode"
                  outlined
                  append-icon="fa-key"
                  v-model="password"
                  hide-details
                  class="mb-4"
                  :disabled="loading"
                ></v-text-field>

                <v-alert
                  v-if="error && !loading"
                  text
                  color="error"
                  icon="fa-solid fa-exclamation-circle"
                  class="font-weight-bold mb-4"
                >{{ error }}</v-alert>

                <v-alert
                  v-else-if="requested && !loading"
                  text
                  color="success"
                  icon="fa-solid fa-check"
                  class="font-weight-bold mb-4"
                >A message with a one-time passcode has been emailed to the address provided. Enter the code to continue.</v-alert>

                <v-btn
                  v-if="requested"
                  block
                  x-large
                  color="primary"
                  class="text-none font-weight-bold text-body-1"
                  type="submit"
                  :loading="loading"
                >Submit Code</v-btn>

                <v-btn
                  v-else
                  block
                  x-large
                  color="primary"
                  class="text-none font-weight-bold text-body-1"
                  type="submit"
                  :loading="loading"
                >Email me a one-time passcode</v-btn>

                <div
                  v-if="requested"
                  class="mt-4"
                >
                  <a
                    class="need-access-link"
                    @click="requested = false"
                  >&lt; Use a different email</a>
                </div>
              </v-form>
            </div>
          </div>

          <div class="accounts-footer d-flex align-center justify-space-between flex-wrap">
            <div class="d-flex align-center status-indicator">
              <span class="status-dot"></span>
              <span class="text-caption text-uppercase font-weight-medium">All Systems Operational</span>
            </div>
            <div class="footer-links">
              <a href="https://cdseo.com/policies/privacy-policy" target="_blank" rel="noopener">Privacy</a>
              <a href="https://cdseo.com/policies/terms-of-service" target="_blank" rel="noopener">Terms</a>
              <a href="https://cdseo.com/pages/contact" target="_blank" rel="noopener">Support</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.auth-wrapper {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 10000000;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;

  .auth-container {
    width: 100%;
    height: 100%;
    .logo-container {
      width: 580px;
      height: 100%;
      background: url('@/assets/logos/icon-lg-login.png') no-repeat right bottom;
      .logo {
        position: absolute;
        padding: 2em;
      }
    }
    .content-container {
      width: calc(100% - 580px);
      height: 100%;
      background-color: #fff;
      overflow-y: auto;
      display: flex;
      align-items: center;
      padding: 3em;
      justify-content: center;
      .logo-container-sm {
        display: none;
      }
      .auth-title {
        line-height: 1.1em;
      }
      .auth-form .v-input:not(.v-input--is-focused) fieldset {
        border-color: #dfe3e8;
      }
      .auth-form .v-input:not(.v-input--is-focused) .v-icon {
        color: #6e8ca2;
      }
      .auth-form .v-input:not(.v-input--is-focused) label {
        color: #6e8ca2;
      }
      .auth-subtitle {
        font-weight: bold;
        color: #0269a5;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 0.85rem;
      }
      .auth-footer {
        font-size: 0.8em;
      }
    }

    // Login screen layout (mirrors the switch-workspace screen)
    .content-container.auth-content-container {
      align-items: stretch;
      justify-content: flex-start;
    }
    .auth-inner {
      margin: 0 auto;
      width: 100%;
      max-width: 860px;
      min-height: 100%;
      display: flex;
      flex-direction: column;
    }
    .auth-top {
      flex-shrink: 0;
    }
    .auth-body {
      flex: 1 0 auto;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }
    .auth-block {
      width: 100%;
      max-width: 600px;
    }
    .auth-lead {
      color: #6e8ca2;
      font-size: 1.05rem;
    }
    .auth-divider-label {
      padding: 0 16px;
      color: #6e8ca2;
      font-size: 0.8rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      white-space: nowrap;
    }
    .field-label {
      color: #6e8ca2;
      font-size: 0.8rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .need-access-link {
      color: #6e8ca2;
      text-decoration: none;
      font-size: 0.95rem;
      cursor: pointer;
    }
    .need-access-link:hover {
      color: #0269a5;
    }

    // Footer — shared look with the switch-workspace screen
    .accounts-footer {
      flex-shrink: 0;
      margin-top: 48px;
      padding-top: 24px;
      padding-bottom: 20px;
    }
    // Bottom padding on each half so the row breathes when it wraps to two
    // lines at narrow widths, and stays balanced when side-by-side.
    .accounts-footer .status-indicator,
    .accounts-footer .footer-links {
      padding-bottom: 12px;
    }
    .status-indicator {
      color: #6e8ca2;
      letter-spacing: 0.06em;
    }
    .status-dot {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background-color: #22c55e;
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.18);
      margin-right: 10px;
    }
    .footer-links a {
      color: #6e8ca2;
      margin-left: 28px;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .footer-links a:first-child {
      margin-left: 0;
    }
    .footer-links a:hover {
      color: #0269a5;
    }
  }
}

@media screen and (max-width: 960px) {
  .auth-wrapper {
    .auth-container {
      width: 100%;
      height: 100%;
      .logo-container {
        display: none;
      }
      .content-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        .logo-container-sm {
          display: block;
        }
      }
    }
  }
}
.theme--dark .content-container {
  background-color: #1e1e1e !important;
}
</style>

<script>
export default {
  data () {
    return {
      loading: false,
      register: false,
      email: null,
      password: null,
      requested: false,
      error: null,
      validated: true, // TEMP for testing
    };
  },
  computed: {
    needAccessUrl () {
      // Identify the workspace by the host we're being served on (e.g.
      // "bp-us.cdseo.com", or "app.cdseo.com" on the app root). Sanitize to
      // valid hostname characters as defense-in-depth; the whole subject is
      // also URL-encoded so nothing can break out of the mailto URL.
      const host = (window.location.hostname || '').toLowerCase().replace(/[^a-z0-9.-]/g, '')

      let subject = 'Requesting Access to CDSEO Workspace'
      if (host) {
        subject += ` for ${host}`
      }
      return 'mailto:help@cdseo.com?subject=' + encodeURIComponent(subject)
    },
    googleAuthUrl () {
      const currentHost = window.location.host
      const subdomain = currentHost.split('.')[0]
      const newHost = `app.${currentHost.split('.').slice(1).join('.')}`
      const state = {
        h: subdomain
      }
      if (Object.entries(this.$route.query).length) {
        state.q = this.$route.query
      } else {
        state.url = window.location.href
      }
      return `${window.location.protocol}//${newHost}/api/auth/google?state=${encodeURIComponent(JSON.stringify(state))}`
    }
  },
  methods: {
    async submit () {
      if (this.requested) {
        return this.login()
      }
      return this.requestPassword()
    },
    async requestPassword () {
      this.error = null
      this.loading = true
      try {
        await this.$http({
          method: 'POST',
          url: '/api/auth/init',
          data: {
            email: this.email,
            ...(this.$route.query.__token
              ? {
                  __token: this.$route.query.__token
                }
              : {}
            ),
            ...(this.$route.query.invitation_token
              ? {
                  invitation_token: this.$route.query.invitation_token
                }
              : {}
            )
          }
        })
        this.requested = true
      } catch (e) {
        this.error = e.response?.data?.error || e.response?.data
      } finally {
        this.loading = false
      }
    },
    async login () {
      this.error = null
      this.loading = true
      try {
        const response = await this.$http({
          method: 'POST',
          url: '/api/auth/local',
          auth: {
            username: this.email,
            password: this.password
          },
          data: {
            ...(this.$route.query.__token
              ? {
                  __token: this.$route.query.__token,
                  _cr: true
                }
              : {}
            ),
            ...(this.$route.query.invitation_token
              ? {
                  invitation_token: this.$route.query.invitation_token
                }
              : {}
            )
          }
        })

        if (this.$route?.query?._r) {
          // TODO: Validate this url to ensure that someone hasn't tampered with the redirect url.
          // In my opinion this is low priority for now and maybe ever, but recommend revisiting later if there's ever a
          // motivation where someone would even want to exploit this (if they even can).
          window.location = this.$route.query._r
        } else {
          this.$store.dispatch('handleAuth', response.data?.data)
        }
      } catch (e) {
        console.log('e', e)
        this.error = (e.response?.status + '') === '401' ? 'The passcode you entered is invalid.' : (e.response?.data?.error || e.response?.data)
        this.loading = false
      }
    },
    reset (value) {
      this.register = value
      this.requested = false
      this.error = false
    }
  }
};
</script>
